import { useEffect, useRef, useState } from "react";
import { Loader2, Mic, MicOff, Camera, Phone, FileText, Siren, ArrowLeft, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api from "../lib/api";
import { speakGenieFR } from "./ReadAloudFAB";

// Top Belgian insurers — quick selection
const INSURERS = [
  { id: "ag", label: "AG Insurance" },
  { id: "axa", label: "AXA Belgium" },
  { id: "ethias", label: "Ethias" },
  { id: "belfius", label: "Belfius Assurances" },
  { id: "kbc", label: "KBC Assurances" },
  { id: "p_et_v", label: "P&V Assurances" },
  { id: "das", label: "DAS" },
  { id: "arag", label: "Arag" },
  { id: "baloise", label: "Baloise" },
  { id: "allianz", label: "Allianz Belgium" },
  { id: "yuzzu", label: "Yuzzu (ex-Touring)" },
  { id: "corona", label: "Corona Direct" },
  { id: "fidea", label: "Fidea" },
  { id: "federale", label: "Fédérale Assurance" },
  { id: "autre", label: "Autre / je ne sais pas" },
];

export default function AccidentFlow({ working, setWorking, onDone }) {
  const [step, setStep] = useState("hub"); // hub | insurer | constat | police | result
  const [audioBlob, setAudioBlob] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [recording, setRecording] = useState(false);
  const [insurer, setInsurer] = useState("");
  const [city, setCity] = useState("");
  const [phoneInfo, setPhoneInfo] = useState(null);
  const [result, setResult] = useState(null);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const videoRef = useRef(null);

  // AUTO-START: camera + mic on mount (the user explicitly requested this)
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: true });
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        // Start audio recorder
        const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
        const mr = new MediaRecorder(stream, { mimeType: mime });
        chunksRef.current = [];
        mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
        mr.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: mime });
          setAudioBlob(blob);
        };
        mr.start();
        recorderRef.current = mr;
        setRecording(true);
        speakGenieFR("Je suis avec vous. Caméra et micro activés. Touchez ce que vous voulez faire.");
      } catch {
        toast.error("Autorisez la caméra et le micro pour utiliser cette fonction");
      }
    })();
    return () => {
      active = false;
      try { recorderRef.current?.stop(); } catch { /* ignore */ }
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    };
  }, []);

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
    setRecording(false);
  };

  const takePhoto = () => {
    if (!videoRef.current || !streamRef.current) {
      toast.error("Caméra non prête");
      return;
    }
    const v = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 1280;
    canvas.height = v.videoHeight || 720;
    canvas.getContext("2d").drawImage(v, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
        setPhotos((p) => [...p, file]);
        toast.success(`Photo ${photos.length + 1} prise`);
      }
    }, "image/jpeg", 0.85);
  };

  // ===== INSURER LOOKUP =====
  const lookupInsurer = async () => {
    if (!insurer) { toast.error("Choisissez votre assureur"); return; }
    setWorking(true);
    try {
      const insLabel = INSURERS.find((i) => i.id === insurer)?.label || insurer;
      const res = await api.post("/sos/accident/insurer-lookup", { insurer: insLabel, city: city.trim() });
      setPhoneInfo(res.data);
      const txt = `${insLabel}. Numéro sinistre : ${res.data.phone_sinistre || "non trouvé"}. Numéro urgence 24/7 : ${res.data.phone_urgence || "non trouvé"}.`;
      speakGenieFR(txt);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setWorking(false); }
  };

  // ===== CONSTAT IA =====
  const submitConstat = async (desc) => {
    setWorking(true);
    try {
      stopRecording();
      // Wait briefly so blob finalizes
      await new Promise((r) => setTimeout(r, 600));
      const fd = new FormData();
      fd.append("description", desc || "");
      if (city.trim()) fd.append("city", city);
      if (audioBlob) fd.append("audio", new File([audioBlob], "accident.webm", { type: audioBlob.type }));
      photos.forEach((p) => fd.append("photos", p));
      const res = await api.post("/sos/accident/constat", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      setResult(res.data);
      onDone(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'analyse");
    } finally { setWorking(false); }
  };

  // ===== HUB — Choice screen (gros boutons, lisibles) =====
  if (step === "hub") {
    return (
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="accident-hub">
        <div className="bg-emerald-800 text-white p-5 flex items-center gap-3">
          <div className="h-12 w-12 rounded-sm bg-white/20 flex items-center justify-center">
            <Siren className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.2em] opacity-80">Touche 7 · Accident</p>
            <h2 className="font-serif-display text-xl font-medium">Je suis là, on s'occupe de tout</h2>
          </div>
        </div>

        {/* Status caméra + micro */}
        <div className="bg-emerald-50 border-b border-emerald-200 px-5 py-3 flex items-center justify-between text-xs">
          <span className="text-emerald-800 font-medium flex items-center gap-1.5">
            {recording ? <><Mic className="h-3.5 w-3.5 text-red-600 animate-pulse" /> Enregistrement en cours</> : <><MicOff className="h-3.5 w-3.5" /> Micro arrêté</>}
          </span>
          <span className="text-emerald-700">📷 {photos.length} photo(s)</span>
        </div>

        {/* Caméra preview compacte */}
        <div className="bg-black aspect-video relative">
          <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={takePhoto}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 h-14 w-14 rounded-full bg-white border-4 border-emerald-700 flex items-center justify-center shadow-lg active:scale-90 transition-transform"
            data-testid="accident-photo"
            aria-label="Prendre une photo"
          >
            <Camera className="h-6 w-6 text-emerald-800" />
          </button>
        </div>

        {/* GROS BOUTONS d'action — pensés pour gros doigts + vue fatiguée */}
        <div className="p-4 sm:p-6 space-y-3" data-testid="accident-actions">
          <ActionButton
            color="bg-emerald-700 hover:bg-emerald-800"
            icon={FileText}
            title="📋 Remplir le constat"
            subtitle="Avec photos et votre voix — l'IA fait le travail"
            onClick={() => setStep("constat")}
            testid="accident-btn-constat"
          />
          <ActionButton
            color="bg-blue-700 hover:bg-blue-800"
            icon={Phone}
            title="📞 Appeler mon assureur"
            subtitle="Le génie trouve le bon numéro et écoute pour vous"
            onClick={() => setStep("insurer")}
            testid="accident-btn-insurer"
          />
          <ActionButton
            color="bg-red-700 hover:bg-red-800"
            icon={Siren}
            title="👮 Appeler la police"
            subtitle="Si la partie adverse refuse de coopérer"
            onClick={() => setStep("police")}
            testid="accident-btn-police"
          />
        </div>
      </div>
    );
  }

  // ===== POLICE =====
  if (step === "police") {
    return (
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="accident-police">
        <FlowHeader title="Appeler la police" subtitle="La partie adverse refuse de coopérer" color="bg-red-700" onBack={() => setStep("hub")} />
        <div className="p-6 space-y-4">
          <div className="bg-red-50 border border-red-300 rounded-sm p-4">
            <p className="text-sm text-red-900 font-medium mb-2">📞 Composez le 101 (police belge)</p>
            <p className="text-xs text-red-800">Dites simplement : <em>« Accident de la route, la partie adverse refuse de remplir le constat ou s'en va. J'ai besoin d'un agent sur place. »</em></p>
          </div>
          <a href="tel:101" className="block w-full bg-red-700 hover:bg-red-800 text-white text-center rounded-sm py-4 text-lg font-medium" data-testid="accident-call-101">
            📞 Appeler le 101 maintenant
          </a>
          <div className="border border-slate-200 rounded-sm p-4 bg-slate-50">
            <Label className="text-xs uppercase tracking-widest mb-2 block">📍 Lieu de l'accident</Label>
            <Input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Ex: Rue de Liège 12, 4000 Liège"
              className="rounded-sm h-12 text-base"
              data-testid="accident-police-city"
            />
            <p className="text-xs text-slate-500 mt-2">Soyez précis — l'agent doit savoir où venir.</p>
          </div>
          <Button onClick={() => submitConstat("Police appelée — partie adverse refuse de coopérer")} disabled={working} className="rounded-sm bg-slate-900 hover:bg-slate-800 h-12 w-full text-base" data-testid="accident-police-record">
            {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Enregistrer l'incident maintenant
          </Button>
        </div>
      </div>
    );
  }

  // ===== INSURER =====
  if (step === "insurer") {
    return (
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="accident-insurer">
        <FlowHeader title="Appeler mon assureur" subtitle="Le génie trouve le bon numéro" color="bg-blue-700" onBack={() => setStep("hub")} />
        <div className="p-6 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block">🏢 Mon assureur</Label>
            <select value={insurer} onChange={(e) => setInsurer(e.target.value)} className="w-full rounded-sm border border-slate-300 bg-white h-12 px-3 text-base" data-testid="accident-insurer-select">
              <option value="">— Choisir —</option>
              {INSURERS.map((i) => (<option key={i.id} value={i.id}>{i.label}</option>))}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block">📍 Ville (optionnel)</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Liège, Bruxelles, Charleroi..." className="rounded-sm h-12 text-base" data-testid="accident-insurer-city" />
          </div>
          <Button onClick={lookupInsurer} disabled={working || !insurer} className="rounded-sm bg-blue-700 hover:bg-blue-800 h-12 w-full text-base" data-testid="accident-insurer-lookup">
            {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            🪔 Trouver le numéro
          </Button>

          {phoneInfo && (
            <div className="bg-blue-50 border-2 border-blue-700 rounded-sm p-4" data-testid="accident-insurer-result">
              <p className="text-xs uppercase tracking-widest text-blue-700 mb-3">🪔 Le génie a trouvé</p>
              {phoneInfo.phone_sinistre && (
                <a href={`tel:${phoneInfo.phone_sinistre}`} className="block bg-blue-700 hover:bg-blue-800 text-white text-center rounded-sm py-3 mb-2 text-base font-medium" data-testid="accident-call-sinistre">
                  📞 Sinistre (heures bureau) : {phoneInfo.phone_sinistre}
                </a>
              )}
              {phoneInfo.phone_urgence && (
                <a href={`tel:${phoneInfo.phone_urgence}`} className="block bg-red-700 hover:bg-red-800 text-white text-center rounded-sm py-3 mb-2 text-base font-medium" data-testid="accident-call-urgence">
                  🚨 Urgence 24/7 : {phoneInfo.phone_urgence}
                </a>
              )}
              {phoneInfo.notes && (<p className="text-xs text-slate-600 mt-2">{phoneInfo.notes}</p>)}
              <p className="text-xs text-amber-700 mt-3 leading-snug">
                💡 Mettez le téléphone sur <strong>haut-parleur</strong> — l'app écoutera l'appel et vérifiera si l'agent dit des choses incorrectes.
              </p>
              <Button onClick={() => submitConstat(`Appel à ${phoneInfo.insurer_label || insurer}`)} disabled={working} className="rounded-sm bg-slate-900 hover:bg-slate-800 h-11 w-full mt-3 text-sm" data-testid="accident-insurer-analyze">
                {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Analyser cet appel avec l'IA
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== CONSTAT =====
  if (step === "constat") {
    return <ConstatForm
      city={city} setCity={setCity}
      photos={photos} takePhoto={takePhoto}
      working={working} onSubmit={submitConstat}
      onBack={() => setStep("hub")}
    />;
  }

  return null;
}

function ActionButton({ color, icon: Icon, title, subtitle, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} className={`w-full ${color} text-white rounded-sm p-5 sm:p-6 text-left flex items-center gap-4 shadow-md active:scale-[0.98] transition-transform min-h-[100px]`} data-testid={testid}>
      <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-sm bg-white/20 flex items-center justify-center flex-shrink-0">
        <Icon className="h-8 w-8 sm:h-9 sm:w-9" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-serif-display text-lg sm:text-xl font-medium leading-tight">{title}</p>
        <p className="text-xs sm:text-sm opacity-90 leading-snug mt-1">{subtitle}</p>
      </div>
    </button>
  );
}

function FlowHeader({ title, subtitle, color, onBack }) {
  return (
    <div className={`${color} text-white p-5 flex items-center gap-3`}>
      <button onClick={onBack} className="h-9 w-9 rounded-sm bg-white/20 flex items-center justify-center" aria-label="Retour" data-testid="flow-back">
        <ArrowLeft className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-[0.2em] opacity-80">{subtitle}</p>
        <h2 className="font-serif-display text-xl font-medium leading-tight">{title}</h2>
      </div>
    </div>
  );
}

function ConstatForm({ city, setCity, photos, takePhoto, working, onSubmit, onBack }) {
  const [desc, setDesc] = useState("");
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="accident-constat">
      <FlowHeader title="Remplir le constat" subtitle="Photos + votre voix · L'IA fait le reste" color="bg-emerald-700" onBack={onBack} />
      <div className="p-5 sm:p-6 space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-widest mb-2 block">📍 Lieu de l'accident</Label>
          <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ex: Carrefour Rue de Liège / Avenue Rogier" className="rounded-sm h-12 text-base" data-testid="constat-city" />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest mb-2 block">🗣️ Décrivez ce qui s'est passé (parlez normalement)</Label>
          <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={5} placeholder="J'étais arrêté au feu rouge, la voiture derrière m'a embouti. Le conducteur est..." className="rounded-sm text-base" data-testid="constat-desc" />
          <p className="text-xs text-slate-500 mt-1">L'audio enregistré depuis le début est aussi pris en compte.</p>
        </div>

        <div className="border border-dashed border-slate-300 rounded-sm p-4 text-center">
          <p className="text-sm font-medium text-slate-700 mb-2">📷 Photos prises : {photos.length}</p>
          <p className="text-xs text-slate-500 mb-3">Conseil : 4 photos minimum (vue large, dégâts proches, plaques, contexte)</p>
          <Button type="button" onClick={takePhoto} variant="outline" className="rounded-sm h-11" data-testid="constat-photo-more">
            <Camera className="h-4 w-4 mr-2" /> Prendre une autre photo
          </Button>
        </div>

        <Button onClick={() => onSubmit(desc)} disabled={working || (!desc.trim() && photos.length === 0)} className="rounded-sm bg-slate-900 hover:bg-slate-800 h-12 w-full text-base" data-testid="constat-submit">
          {working ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyse en cours…</> : <><ShieldCheck className="h-4 w-4 mr-2" />Générer le constat IA</>}
        </Button>
      </div>
    </div>
  );
}
